import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { useCan, useFeature } from "@/lib/policy";
import { orpc } from "@/lib/orpc";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { readState } from "@/lib/query/read-state";
import { ActivityCard } from "./-components/activity-card";
import { CampaignReturnTable } from "./-components/campaign-return-table";
import { CrmActions } from "./-components/crm-actions";
import { EventReturnTable } from "./-components/event-return-table";
import { FollowUpCard } from "./-components/follow-up-card";
import { WINDOWS, type WindowDays } from "./-components/format";
import { PeriodSection } from "./-components/period-section";
import { PipelineBoard } from "./-components/pipeline-board";
import { ReasonCard } from "./-components/reason-card";
import { RecentLeads } from "./-components/recent-leads";
import { ShelbySection } from "./-components/shelby-section";
import { SourceCard } from "./-components/source-card";
import { TeamTable } from "./-components/team-table";
import { TodayTiles } from "./-components/today-tiles";
import { TourCard } from "./-components/tour-card";

export const Route = createFileRoute("/_authenticated/_crm/crm/")({
  validateSearch: (search: Record<string, unknown>): { days?: WindowDays } => ({
    days: WINDOWS.find((days) => days === Number(search.days)),
  }),
  component: CrmDashboard,
});

// A card that reads missing data as "none yet" is replaced when its read failed, never drawn empty.
const failedCard = (
  message: string,
  read: { error: Error | null; isFetching: boolean; refetch: () => unknown },
) => (
  <Card className="py-0">
    <ReadFailed
      className="p-4"
      message={message}
      error={read.error}
      retrying={read.isFetching}
      onRetry={() => void read.refetch()}
    />
  </Card>
);

/** The sales manager's morning: the pipeline, today's lists, new enquiries, then the chosen
 * window. */
function CrmDashboard() {
  const { facilityId } = useFacility();
  const days = Route.useSearch().days ?? 90;
  const navigate = useNavigate({ from: Route.fullPath });
  const input = { input: { facilityId } };
  const windowed = { input: { facilityId, days } };

  const overview = useQuery(orpc.crm.analytics.overview.queryOptions(input));
  const funnel = useQuery(orpc.crm.analytics.funnel.queryOptions(input));
  const sources = useQuery(orpc.crm.analytics.bySource.queryOptions(input));
  // The last window stays up while the next is read, so switching never blanks the page.
  const trend = useQuery({
    ...orpc.crm.analytics.trend.queryOptions(windowed),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const tours = useQuery(orpc.crm.analytics.tours.queryOptions(windowed));
  const team = useQuery(orpc.crm.analytics.team.queryOptions(windowed));
  const activity = useQuery(orpc.crm.analytics.activity.queryOptions(windowed));
  const lost = useQuery(
    orpc.crm.analytics.outcomeReasons.queryOptions({ input: { facilityId, status: "lost" } }),
  );
  const won = useQuery(
    orpc.crm.analytics.outcomeReasons.queryOptions({ input: { facilityId, status: "converted" } }),
  );
  const campaigns = useQuery(orpc.crm.analytics.campaignReturn.queryOptions(input));
  const hasEvents = useFeature("crm-events");
  const events = useQuery({
    ...orpc.crm.events.returns.queryOptions({ input: { facilityId } }),
    enabled: hasEvents,
  });
  const hasShelby = useFeature("shelby-conversations");
  const canReadShelby = useCan("salesagent:read");
  const canSeeShelby = hasShelby && canReadShelby;
  const shelby = useQuery({
    ...orpc.shelby.analytics.queryOptions(windowed),
    enabled: canSeeShelby,
  });

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  if (overview.isPending || trend.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="CRM dashboard" meta={today} actions={<CrmActions />} />
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={`row-${index}`} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }
  // Without the two reads the page is built on, every card below would read as "none yet".
  if (overview.isError || trend.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="CRM dashboard" actions={<CrmActions />} />
        <Card className="py-0">
          <ReadFailed
            className="p-4"
            message="Couldn't load the CRM numbers. Nothing is shown because nothing was read."
            error={overview.error ?? trend.error}
            retrying={overview.isFetching || trend.isFetching}
            onRetry={() => {
              void overview.refetch();
              void trend.refetch();
            }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="CRM dashboard"
        meta={`${today}, ${overview.data.activeLeads} open ${overview.data.activeLeads === 1 ? "lead" : "leads"}`}
        actions={<CrmActions />}
      />
      {readState(funnel) === "failed" ? (
        failedCard("Couldn't load the pipeline.", funnel)
      ) : (
        <PipelineBoard facilityId={facilityId} overview={overview.data} funnel={funnel.data} />
      )}
      <TodayTiles
        facilityId={facilityId}
        activity={activity.data}
        activityState={{
          pending: activity.isPending,
          error: activity.error,
          retrying: activity.isFetching,
          retry: () => void activity.refetch(),
        }}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <RecentLeads facilityId={facilityId} />
        </div>
        <div className="lg:col-span-2">
          {readState(sources) === "failed" ? (
            failedCard("Couldn't load where leads come from.", sources)
          ) : (
            <SourceCard sources={sources.data} />
          )}
        </div>
      </div>

      <PeriodSection
        trend={trend.data}
        days={days}
        onDays={(next) => navigate({ search: { days: next } })}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {readState(tours) === "failed" ? (
          failedCard("Couldn't load the tour numbers.", tours)
        ) : (
          <TourCard data={tours.data} />
        )}
        <ActivityCard data={activity.data} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          {readState(team) === "failed" ? (
            failedCard("Couldn't load the team numbers.", team)
          ) : (
            <TeamTable data={team.data} />
          )}
        </div>
        {/* "Gone quiet" above links here for the rest of its list. */}
        <div id="follow-up" className="scroll-mt-20 lg:col-span-2">
          <FollowUpCard data={activity.data} />
        </div>
      </div>

      {canSeeShelby ? <ShelbySection data={shelby.data} /> : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {readState(lost) === "failed" ? (
          failedCard("Couldn't load why referrals were lost.", lost)
        ) : (
          <ReasonCard
            title="Why referrals were lost"
            subtitle="Recorded when a lead is marked Lost"
            emptyTitle="No lost reasons yet"
            emptyDetail="Reasons appear here once leads are marked Lost."
            rows={lost.data}
          />
        )}
        {readState(won) === "failed" ? (
          failedCard("Couldn't load what won admissions.", won)
        ) : (
          <ReasonCard
            title="What won admissions"
            subtitle="Recorded when a lead converts"
            emptyTitle="No won reasons yet"
            emptyDetail="Reasons appear here once leads convert."
            rows={won.data}
          />
        )}
      </div>

      {readState(campaigns) === "failed" ? (
        failedCard("Couldn't load the campaign returns.", campaigns)
      ) : (
        <CampaignReturnTable rows={campaigns.data} />
      )}
      {hasEvents && readState(events) === "failed"
        ? failedCard("Couldn't load the event returns.", events)
        : null}
      {hasEvents && readState(events) !== "failed" ? (
        <EventReturnTable rows={events.data?.rows} />
      ) : null}
    </div>
  );
}
